import React, { useEffect, useMemo, useState } from 'react';
import './Reservations.css';
import Table from '../../components/common/Table';
import { getExpenses } from '../../services/expenseService';
import { getAllServices } from '../../services/serviceService';
import { formatDate } from '../../utils/date';
import {
  MONTHS,
  getMonthRange,
  hasNumeric,
  getDisplayMoney,
  filterExpensesByRange,
  filterServicesByRange,
  computeProfitabilityTotals
} from '../../utils/profitability';
import { IconCalendar } from '../../components/common/Icons';
import ProfitabilityHistoryModal from '../../components/modals/ProfitabilityHistoryModal';

const formatAmount = (value, currency) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (Number.isNaN(n)) return '-';
  return currency === '$' ? n.toFixed(0) : n.toFixed(2);
};

const formatMoney = (value, currency) => {
  const amount = formatAmount(value, currency);
  if (amount === '-') return '-';
  return currency ? `${currency} ${amount}` : amount;
};

const formatTotal = (total, currency) => Number(total || 0).toLocaleString('es-AR', {
  minimumFractionDigits: currency === '$' ? 0 : 2,
  maximumFractionDigits: currency === '$' ? 0 : 2
});

const ProfitabilityPage = () => {
  const now = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [convertPesosToUsd, setConvertPesosToUsd] = useState(true);

  const [expenses, setExpenses] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);

  const yearOptions = useMemo(() => {
    const y = Number(year);
    const start = Math.min(y - 5, now.getFullYear() - 5);
    const end = Math.max(y + 1, now.getFullYear() + 1);
    const years = [];
    for (let i = end; i >= start; i -= 1) years.push(i);
    return years;
  }, [year, now]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [exp, svc] = await Promise.all([getExpenses(), getAllServices()]);
      setExpenses(exp || []);
      setServices(svc || []);
    } catch (err) {
      console.error(err);
      setError('Error al cargar rentabilidad');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const range = useMemo(() => getMonthRange(year, month), [year, month]);

  const filteredExpenses = useMemo(
    () => filterExpensesByRange(expenses, range.from, range.to),
    [expenses, range.from, range.to]
  );

  const filteredServices = useMemo(
    () => filterServicesByRange(services, range.from, range.to),
    [services, range.from, range.to]
  );

  const totals = useMemo(
    () => computeProfitabilityTotals(filteredExpenses, filteredServices, convertPesosToUsd),
    [filteredExpenses, filteredServices, convertPesosToUsd]
  );

  const expensesColumns = useMemo(() => ([
    {
      id: 'date',
      header: 'Fecha',
      accessor: (e) => formatDate(e.date, ''),
      sortAccessor: (e) => e.date || ''
    },
    {
      id: 'description',
      header: 'Descripción',
      accessor: (e) => e.description || ''
    },
    {
      id: 'isFromFede',
      header: '',
      sortable: false,
      filterable: false,
      headerClassName: 'expense-from-fede-column',
      cell: (e) => Number(e.isFromFede) === 1
        ? <span className="expense-from-fede-marker" aria-label="Gasto de Fede">F</span>
        : null
    },
    {
      id: 'money',
      header: 'Importe',
      accessor: (e) => {
        const display = getDisplayMoney(e.value, e.currency, e.conversion, convertPesosToUsd);
        if (!display) return '-';
        return formatMoney(display.amount, display.currency);
      },
      sortComparator: (a, b) => {
        const ca = a?.currency ?? '';
        const cb = b?.currency ?? '';
        const cRes = String(ca).localeCompare(String(cb), undefined, { sensitivity: 'base' });
        if (cRes !== 0) return cRes;
        const va = Number(a?.value);
        const vb = Number(b?.value);
        const aNaN = Number.isNaN(va);
        const bNaN = Number.isNaN(vb);
        if (aNaN && bNaN) return 0;
        if (aNaN) return 1;
        if (bNaN) return -1;
        return va - vb;
      }
    }
  ]), [convertPesosToUsd]);

  const profitabilityColumns = useMemo(() => ([
    {
      id: 'client',
      header: 'Cliente',
      accessor: (s) => `${s.client_surname || ''}, ${s.client_name || ''}`.replace(/^,\s*$/, '-'),
      sortAccessor: (s) => `${s.client_surname || ''} ${s.client_name || ''}`.trim()
    },
    {
      id: 'type',
      header: 'Tipo',
      accessor: (s) => s.type || '-'
    },
    {
      id: 'money',
      header: 'Importe',
      accessor: (s) => {
        if (!hasNumeric(s.price) || !hasNumeric(s.cost)) return '-';
        const bankExpense = hasNumeric(s.bank_expense ?? s.bankExpense) ? Number(s.bank_expense ?? s.bankExpense) : 0;
        const profit = Number(s.price) - Number(s.cost) - bankExpense;
        const display = getDisplayMoney(profit, s.currency, s.conversion, convertPesosToUsd);
        if (!display) return '-';
        return formatMoney(display.amount, display.currency);
      },
      sortComparator: (a, b) => {
        const ca = a?.currency ?? '';
        const cb = b?.currency ?? '';
        const cRes = String(ca).localeCompare(String(cb), undefined, { sensitivity: 'base' });
        if (cRes !== 0) return cRes;
        const aBankExpense = hasNumeric(a?.bank_expense ?? a?.bankExpense) ? Number(a.bank_expense ?? a.bankExpense) : 0;
        const bBankExpense = hasNumeric(b?.bank_expense ?? b?.bankExpense) ? Number(b.bank_expense ?? b.bankExpense) : 0;
        const pa = hasNumeric(a?.price) && hasNumeric(a?.cost) ? (Number(a.price) - Number(a.cost) - aBankExpense) : NaN;
        const pb = hasNumeric(b?.price) && hasNumeric(b?.cost) ? (Number(b.price) - Number(b.cost) - bBankExpense) : NaN;
        const aNaN = Number.isNaN(pa);
        const bNaN = Number.isNaN(pb);
        if (aNaN && bNaN) return 0;
        if (aNaN) return 1;
        if (bNaN) return -1;
        return pa - pb;
      }
    }
  ]), [convertPesosToUsd]);

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <div className="actions-row">
          <h1>Rentabilidad</h1>
          <label className="profitability-switch">
            <input
              type="checkbox"
              checked={convertPesosToUsd}
              onChange={(e) => setConvertPesosToUsd(e.target.checked)}
            />
            <span className="profitability-switch-slider" />
          </label>
        </div>
        <div className="profitability-filters-col">
          <div className="actions-row" style={{ justifyContent: 'flex-end' }}>
            <div className="actions-row">
              <b>Mes</b>
              <select
                className="search-input"
                style={{ minWidth: 160 }}
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div className="actions-row">
              <b>Año</b>
              <select
                className="search-input"
                style={{ minWidth: 120 }}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="actions-row" style={{ justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn secondary btn--icon"
              onClick={() => setHistoryOpen(true)}
            >
              <IconCalendar />
              Histórico
            </button>
          </div>
        </div>
      </div>

      {error && <div className="form-error-message" role="alert">{error}</div>}

      {loading ? (
        <p>Cargando...</p>
      ) : (
        <>
          <div className="profitability-grid">
            <div className="section-card" style={{ marginBottom: 0 }}>
              <h3 style={{ margin: 0 }}>Gastos</h3>

              <Table
                data={filteredExpenses}
                columns={expensesColumns}
                rowKey="id"
                className="table expenses-table"
                emptyMessage="No hay gastos para mostrar."
              />
            </div>

            <div className="section-card" style={{ marginBottom: 0 }}>
              <h3 style={{ margin: 0 }}>Rentabilidades</h3>

              <Table
                data={filteredServices}
                columns={profitabilityColumns}
                rowKey="id"
                className="table"
                emptyMessage="No hay servicios para mostrar."
              />
            </div>
          </div>

          <div className="profitability-totals">
            <div className="total-card total-card--expense">
              <span className="total-card-label">Total gastos</span>
              <div className="total-card-values">
                {totals.expenses.length === 0 ? (
                  <span className="total-empty">-</span>
                ) : (
                  totals.expenses.map(({ currency, total }) => (
                    <div key={currency} className="total-amount total-amount--negative">
                      <span className="total-currency">{currency}</span>
                      <span className="total-value">{formatTotal(total, currency)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="total-card total-card--profit">
              <span className="total-card-label">Total rentabilidad</span>
              <div className="total-card-values">
                {totals.profitability.length === 0 ? (
                  <span className="total-empty">-</span>
                ) : (
                  totals.profitability.map(({ currency, total }) => (
                    <div key={currency} className="total-amount total-amount--positive">
                      <span className="total-currency">{currency}</span>
                      <span className="total-value">{formatTotal(total, currency)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="total-card total-card--net">
              <span className="total-card-label">Total ganancia</span>
              <div className="total-card-values">
                {totals.net.length === 0 ? (
                  <span className="total-empty">-</span>
                ) : (
                  totals.net.map(({ currency, total }) => (
                    <div
                      key={currency}
                      className={`total-amount ${Number(total) > 0 ? 'total-amount--positive' : Number(total) < 0 ? 'total-amount--negative' : ''}`}
                    >
                      <span className="total-currency">{currency}</span>
                      <span className="total-value">{formatTotal(total, currency)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <ProfitabilityHistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        expenses={expenses}
        services={services}
        convertPesosToUsd={convertPesosToUsd}
        initialYear={year}
      />
    </div>
  );
};

export default ProfitabilityPage;

